import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    BRAIN_GAME_DURATION_MS,
    BrainGameHighScoreContext,
    BrainGameHeader,
    GAME_ROUTE_PREFIX,
    GameResultOverlay,
    TimeUpOverlay,
    TutorialModal,
    clamp,
    useTicker,
} from './shared';
import {
    BRAIN_TRAINING_GAME_MENU_ITEMS,
    GAME_BY_ID,
    getBrainTrainingGameMenuItems,
    getBrainTrainingGameFromHash,
    getBrainTrainingGameHref,
} from './registry';
import { BrainTrainingGamesStyles } from './styles';
import { getBrainGameUiCopy, getLocalizedBrainGame } from '../../i18n/brainGameLocales';
import brainGameHighScores from '../../lib/brainGameHighScores';

const { evaluateBrainGameHighScore } = brainGameHighScores;

function BrainTrainingGames({
    brainGameHighScores = {},
    colorBlindModeEnabled = false,
    initialGameId = '',
    initialLevel = null,
    language = 'en',
    onClose,
    onRunComplete = null,
}) {
    const [activeGameId, setActiveGameId] = useState(() => (
        initialGameId && GAME_BY_ID[initialGameId] ? initialGameId : ''
    ));
    const [gameStarted, setGameStarted] = useState(false);
    const [tutorialOpen, setTutorialOpen] = useState(false);
    const [runStartedAt, setRunStartedAt] = useState(null);
    const [runKey, setRunKey] = useState(0);
    const [runExpired, setRunExpired] = useState(false);
    const [runPausedAt, setRunPausedAt] = useState(null);
    const [runPausedDurationMs, setRunPausedDurationMs] = useState(0);
    const [runStartLevel, setRunStartLevel] = useState(null);
    const [runBestStreak, setRunBestStreak] = useState(0);
    const [manualFinished, setManualFinished] = useState(false);
    const [metrics, setMetrics] = useState({});
    const [runHighScoreResult, setRunHighScoreResult] = useState(null);
    const runCompletionReportedRef = useRef(false);
    const tutorialTriggerRef = useRef(null);
    const activeGame = activeGameId ? getLocalizedBrainGame(GAME_BY_ID[activeGameId], language) : null;
    const hasRequestedInitialLevel = initialLevel !== null && initialLevel !== undefined && initialLevel !== '';
    const requestedInitialLevel = Number(initialLevel);
    const normalizedInitialLevel = activeGame
        ? clamp(
            hasRequestedInitialLevel && Number.isFinite(requestedInitialLevel)
                ? Math.round(requestedInitialLevel)
                : activeGame.defaultLevel,
            activeGame.minLevel,
            activeGame.maxLevel,
        )
        : null;
    const sharedCopy = getBrainGameUiCopy(language);
    const ActiveComponent = activeGame?.Component;
    const usesRunTimer = activeGame?.hud?.timer === true;
    const usesInternalTimer = activeGameId === 'dual-track';
    const canManuallyFinish = !usesRunTimer && !usesInternalTimer;
    const now = useTicker(Boolean(
        gameStarted
        && usesRunTimer
        && runStartedAt
        && !runExpired
        && !manualFinished
        && !tutorialOpen
    ));
    const effectiveRunNow = runPausedAt || now;
    const timeRemainingMs = gameStarted && usesRunTimer && runStartedAt
        ? clamp(
            BRAIN_GAME_DURATION_MS - (effectiveRunNow - runStartedAt - runPausedDurationMs),
            0,
            BRAIN_GAME_DURATION_MS,
        )
        : null;
    const runTimedOut = usesRunTimer && gameStarted && runStartedAt && (runExpired || timeRemainingMs <= 0);

    useEffect(() => {
        const handleHashChange = () => {
            const gameId = getBrainTrainingGameFromHash(window.location.hash);
            if (gameId && GAME_BY_ID[gameId]) {
                setActiveGameId(gameId);
                return;
            }

            onClose?.();
        };

        window.addEventListener('hashchange', handleHashChange);
        return () => window.removeEventListener('hashchange', handleHashChange);
    }, [onClose]);

    useEffect(() => {
        if (!activeGameId) {
            return;
        }

        setGameStarted(false);
        setTutorialOpen(true);
        setRunStartedAt(null);
        setRunKey(0);
        setRunExpired(false);
        setRunPausedAt(null);
        setRunPausedDurationMs(0);
        setRunStartLevel(null);
        setRunBestStreak(0);
        setManualFinished(false);
        setMetrics({});
        setRunHighScoreResult(null);
        runCompletionReportedRef.current = false;
    }, [activeGameId]);

    useEffect(() => {
        if (usesRunTimer && gameStarted && runStartedAt && timeRemainingMs <= 0) {
            setRunExpired(true);
        }
    }, [gameStarted, runStartedAt, timeRemainingMs, usesRunTimer]);

    const close = (event) => {
        event?.preventDefault?.();

        if (window.location.hash.startsWith(GAME_ROUTE_PREFIX)) {
            window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
        }
        onClose?.();
    };

    const startRun = useCallback(() => {
        setRunStartedAt(Date.now());
        setRunKey((value) => value + 1);
        setRunExpired(false);
        setRunPausedAt(null);
        setRunPausedDurationMs(0);
        setRunStartLevel(null);
        setRunBestStreak(0);
        setManualFinished(false);
        setMetrics({});
        setRunHighScoreResult(null);
        runCompletionReportedRef.current = false;
        setGameStarted(true);
        setTutorialOpen(false);
    }, []);

    const handleMetricsChange = useCallback((nextMetrics = {}) => {
        const normalizedMetrics = nextMetrics && typeof nextMetrics === 'object' ? nextMetrics : {};
        const nextKey = JSON.stringify(normalizedMetrics);

        if (Number.isFinite(normalizedMetrics.level) && normalizedMetrics.level > 0) {
            setRunStartLevel((currentLevel) => (
                currentLevel === null ? Math.round(normalizedMetrics.level) : currentLevel
            ));
        }

        if (Number.isFinite(normalizedMetrics.streak)) {
            setRunBestStreak((currentBest) => Math.max(currentBest, normalizedMetrics.streak));
        }

        setMetrics((currentMetrics) => (
            JSON.stringify(currentMetrics) === nextKey ? currentMetrics : normalizedMetrics
        ));
    }, []);

    const reportRunComplete = useCallback((reason, finalMetrics = {}) => {
        if (runCompletionReportedRef.current) {
            return;
        }

        runCompletionReportedRef.current = true;
        const completionMetrics = {
            ...metrics,
            ...(finalMetrics && typeof finalMetrics === 'object' ? finalMetrics : {}),
        };
        if (!Number.isFinite(completionMetrics.elapsedMs) && runStartedAt) {
            completionMetrics.elapsedMs = Math.max(
                0,
                (runPausedAt || Date.now()) - runStartedAt - runPausedDurationMs,
            );
        }
        if (Number.isFinite(completionMetrics.streak) || Number.isFinite(runBestStreak)) {
            completionMetrics.streak = Math.max(
                Number.isFinite(completionMetrics.streak) ? completionMetrics.streak : 0,
                Number.isFinite(runBestStreak) ? runBestStreak : 0,
            );
        }
        const highScoreResult = evaluateBrainGameHighScore(
            brainGameHighScores,
            activeGameId,
            activeGame?.hud || {},
            completionMetrics,
        );

        setRunHighScoreResult(highScoreResult);
        onRunComplete?.({
            gameId: activeGameId,
            highScore: highScoreResult.best,
            performance: highScoreResult.candidate,
            metrics: completionMetrics,
            reason,
        });
    }, [
        activeGameId,
        activeGame,
        brainGameHighScores,
        metrics,
        onRunComplete,
        runBestStreak,
        runPausedAt,
        runPausedDurationMs,
        runStartedAt,
    ]);

    useEffect(() => {
        if (!runTimedOut) {
            return;
        }

        reportRunComplete('timeout', { durationMs: BRAIN_GAME_DURATION_MS });
    }, [reportRunComplete, runTimedOut]);

    const handleNaturalRunComplete = useCallback((finalMetrics = {}) => {
        handleMetricsChange(finalMetrics);
        reportRunComplete('natural', finalMetrics);
    }, [handleMetricsChange, reportRunComplete]);

    const handleRunRestart = useCallback(() => {
        runCompletionReportedRef.current = false;
        setRunStartedAt(Date.now());
        setRunPausedAt(null);
        setRunPausedDurationMs(0);
        setRunStartLevel(null);
        setRunBestStreak(0);
        setMetrics({});
        setRunHighScoreResult(null);
    }, []);

    const finishRun = useCallback(() => {
        if (!gameStarted || manualFinished || runTimedOut) {
            return;
        }

        reportRunComplete('manual', metrics);
        setManualFinished(true);
    }, [gameStarted, manualFinished, metrics, reportRunComplete, runTimedOut]);

    const openTutorial = () => {
        if (gameStarted && !runTimedOut && !manualFinished) {
            setRunPausedAt((currentPausedAt) => currentPausedAt || Date.now());
        }
        setTutorialOpen(true);
    };

    const closeTutorial = () => {
        if (!gameStarted || runTimedOut) {
            startRun();
            return;
        }

        if (runPausedAt) {
            setRunPausedDurationMs((duration) => duration + Math.max(0, Date.now() - runPausedAt));
            setRunPausedAt(null);
        }
        setTutorialOpen(false);
    };

    if (!activeGame || !ActiveComponent) {
        return null;
    }

    const plainGameIds = ['chance-cards', 'turn-match', 'number-rain', 'order-rush', 'estimate-duel', 'new-catch'];
    const screenClassName = [
        'UFOV-brain-games__screen',
        activeGame.plain || plainGameIds.includes(activeGameId) ? 'is-plain-game' : '',
        activeGameId === 'dual-track' ? 'is-dual-track-game' : '',
        colorBlindModeEnabled ? 'is-color-blind-mode' : '',
    ].filter(Boolean).join(' ');

    return (
        <BrainGameHighScoreContext.Provider value={runHighScoreResult}>
        <section className={screenClassName} aria-label={sharedCopy.regionLabel || 'Brain games'}>
            <BrainGameHeader
                actionsEnabled={!tutorialOpen}
                activeGame={activeGame}
                copy={sharedCopy}
                gameStarted={gameStarted}
                infoButtonRef={tutorialTriggerRef}
                metrics={metrics}
                onClose={close}
                onInfo={openTutorial}
                timeRemainingMs={usesRunTimer ? timeRemainingMs : metrics.timeRemainingMs}
            />
            {gameStarted && !runTimedOut && !manualFinished ? (
                <ActiveComponent
                    colorBlindModeEnabled={colorBlindModeEnabled}
                    copy={sharedCopy}
                    game={activeGame}
                    hud={activeGame.hud}
                    initialLevel={normalizedInitialLevel}
                    isPaused={tutorialOpen}
                    key={`${activeGameId}-${normalizedInitialLevel}-${runKey}`}
                    onClose={close}
                    onMetricsChange={handleMetricsChange}
                    onRunComplete={handleNaturalRunComplete}
                    onRunRestart={handleRunRestart}
                    title={activeGame.title}
                    focus={activeGame.focus}
                    summary={activeGame.summary}
                />
            ) : null}
            {gameStarted && canManuallyFinish && !runTimedOut && !manualFinished && !tutorialOpen ? (
                <button className="UFOV-brain-games__finish-run" onClick={finishRun} type="button">
                    {sharedCopy.finishRun || 'Finish run'}
                </button>
            ) : null}
            {runTimedOut && !tutorialOpen ? (
                <TimeUpOverlay
                    copy={sharedCopy}
                    game={activeGame}
                    hud={activeGame.hud}
                    levelStart={runStartLevel}
                    metrics={{ ...metrics, streak: Math.max(runBestStreak, metrics.streak || 0) }}
                    onClose={close}
                    onRestart={startRun}
                />
            ) : null}
            {manualFinished && !tutorialOpen ? (
                <GameResultOverlay
                    copy={sharedCopy}
                    game={activeGame}
                    hud={activeGame.hud}
                    levelStart={runStartLevel}
                    metrics={{ ...metrics, streak: Math.max(runBestStreak, metrics.streak || 0) }}
                    onClose={close}
                    onRestart={startRun}
                />
            ) : null}
            {tutorialOpen ? (
                <TutorialModal
                    copy={sharedCopy}
                    game={activeGame}
                    initialLevel={normalizedInitialLevel}
                    onClose={closeTutorial}
                    restoreFocusRef={tutorialTriggerRef}
                />
            ) : null}
        </section>
        </BrainGameHighScoreContext.Provider>
    );
}



export {
    BRAIN_TRAINING_GAME_MENU_ITEMS,
    BrainTrainingGamesStyles,
    getBrainTrainingGameMenuItems,
    getBrainTrainingGameFromHash,
    getBrainTrainingGameHref,
};

export default BrainTrainingGames;
